import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle, 
  FileText, 
  RefreshCw,
  Calendar,
  Filter,
  Search,
  User,
  UserCheck,
  Timer,
  Loader2
} from 'lucide-react';
import { useTheme } from '../zustand/ThemeStore';
import { useUserStore } from '../zustand/UserStore';
import { getApiBaseUrl } from '../utils/config';

interface Job {
  job_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress_step?: 'processing_files' | 'generating_transcript' | 'analyzing_call';
  progress_message?: string;
  total_files: number;
  processed_files: number;
  representative_name: string;
  user_id?: string;
  user_name?: string;
  created_at: any;
  completed_at?: any;
  error_message?: string;
}

const JobsPage: React.FC = () => {
  const isDarkMode = useTheme((state) => state.theme);
  const currentUser = useUserStore((state) => state.user);
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchJobs();
    }
  }, [currentUser]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      console.log('Fetching jobs for user:', currentUser?.uid);
      
      // Determine if user should see hierarchical jobs based on role
      const userRole = currentUser?.role?.toLowerCase()?.trim() || '';
      const userDesignation = currentUser?.designation?.toLowerCase()?.trim() || '';
      
      const isAdmin = userRole.includes('admin') || userDesignation.includes('admin');
      const isSuperAdmin = userRole.includes('super') || userDesignation.includes('super');
      
      // Use hierarchical endpoint for admins and super admins, regular endpoint for users
      const endpoint = (isAdmin || isSuperAdmin) 
        ? `${getApiBaseUrl()}/api/v1/jobs/hierarchical`
        : `${getApiBaseUrl()}/api/v1/jobs`;
      
      console.log('Using endpoint:', endpoint, 'for role:', userRole, 'designation:', userDesignation);
      
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('Jobs API response:', response.data);
      const jobsData = response.data.jobs || [];
      console.log('Jobs data:', jobsData);
      
      setJobs(jobsData);
    } catch (error: any) {
      console.error('Error fetching jobs:', error);
      console.error('Error response:', error?.response?.data);
      toast.error('Failed to load jobs', {
        description: error?.response?.data?.detail || 'Unable to fetch job data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchJobs();
    setIsRefreshing(false);
    toast.success('Jobs refreshed');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'queued':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return isDarkMode ? 'text-green-400 bg-green-900/20' : 'text-green-700 bg-green-100';
      case 'failed':
        return isDarkMode ? 'text-red-400 bg-red-900/20' : 'text-red-700 bg-red-100';
      case 'processing':
        return isDarkMode ? 'text-blue-400 bg-blue-900/20' : 'text-blue-700 bg-blue-100';
      case 'queued':
        return isDarkMode ? 'text-yellow-400 bg-yellow-900/20' : 'text-yellow-700 bg-yellow-100';
      default:
        return isDarkMode ? 'text-gray-400 bg-gray-900/20' : 'text-gray-700 bg-gray-100';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date: Date;
      
      if (timestamp?.seconds) {
        // Firestore timestamp with seconds
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
        // Firestore timestamp object with toDate method
        date = timestamp.toDate();
      } else if (timestamp?._seconds) {
        // Alternative Firestore timestamp format
        date = new Date(timestamp._seconds * 1000);
      } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        // Regular date string or number
        date = new Date(timestamp);
      } else {
        // Fallback for unexpected formats
        console.warn('Unexpected timestamp format:', timestamp);
        return 'Invalid Date';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date created from timestamp:', timestamp);
        return 'Invalid Date';
      }
      
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'Timestamp:', timestamp);
      return 'Error';
    }
  };

  const getDateFilterOptions = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    return { today, yesterday, weekAgo, monthAgo };
  };

  const filterJobs = () => {
    let filtered = jobs;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(job => 
        job.representative_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.job_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const { today, yesterday, weekAgo, monthAgo } = getDateFilterOptions();
      
      filtered = filtered.filter(job => {
        if (!job.created_at) return false;
        
        try {
          let jobDate: Date;
          if (job.created_at?.seconds) {
            jobDate = new Date(job.created_at.seconds * 1000);
          } else if (job.created_at?.toDate && typeof job.created_at.toDate === 'function') {
            jobDate = job.created_at.toDate();
          } else if (job.created_at?._seconds) {
            jobDate = new Date(job.created_at._seconds * 1000);
          } else {
            jobDate = new Date(job.created_at);
          }

          // Check if date is valid
          if (isNaN(jobDate.getTime())) {
            console.warn('Invalid job date:', job.created_at);
            return false;
          }

          switch (dateFilter) {
            case 'today':
              return jobDate >= today;
            case 'yesterday':
              return jobDate >= yesterday && jobDate < today;
            case 'week':
              return jobDate >= weekAgo;
            case 'month':
              return jobDate >= monthAgo;
            default:
              return true;
          }
        } catch (error) {
          console.error('Error filtering by date:', error, 'Job:', job);
          return false;
        }
      });
    }

    return filtered;
  };

  const filteredJobs = filterJobs();

  const handleViewJob = (jobId: string) => {
    navigate(`/job-results/${jobId}`);
  };

  const getUserDisplayName = (job: Job) => {
    if (!job.user_id) return 'Unknown User';
    if (job.user_id === currentUser?.uid) return 'You';
    
    return job.user_name || `User ${job.user_id.slice(0, 8)}...`;
  };

  const shouldShowCreatedByColumn = () => {
    const userRole = currentUser?.role?.toLowerCase()?.trim() || '';
    const userDesignation = currentUser?.designation?.toLowerCase()?.trim() || '';
    return userRole.includes('admin') || userDesignation.includes('admin');
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="p-6">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#1a9f6b]" />
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Loading jobs...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Analysis Jobs
              </h1>
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {shouldShowCreatedByColumn() 
                  ? 'Track and manage analysis jobs from your team'
                  : 'Track and manage your analysis jobs'
                }
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-[#1a9f6b] hover:bg-[#148856] text-black'
                  : 'bg-[#1a9f6b] hover:bg-[#148856] text-white'
              } disabled:opacity-50`}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className={`p-4 rounded-lg border ${
            isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
          }`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by representative or job ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                    isDarkMode
                      ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-400'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b]/50`}
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                    isDarkMode
                      ? 'bg-[#050a0a] border-[#1a2424] text-white'
                      : 'bg-gray-50 border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b]/50`}
                >
                  <option value="all">All Status</option>
                  <option value="queued">Queued</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Date Filter */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                    isDarkMode
                      ? 'bg-[#050a0a] border-[#1a2424] text-white'
                      : 'bg-gray-50 border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-[#1a9f6b]/50`}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">Last 7 days</option>
                  <option value="month">Last 30 days</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Jobs Table */}
        <div className={`rounded-lg border overflow-hidden ${
          isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
        }`}>
          {filteredJobs.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                No jobs found
              </p>
              <p className={`${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {jobs.length === 0 
                  ? "You haven't started any analysis jobs yet."
                  : "Try adjusting your filters to find jobs."
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Job
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Status
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Progress
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Representative
                    </th>
                    {shouldShowCreatedByColumn() && (
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Created By
                      </th>
                    )}
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Created
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-[#1a2424]' : 'divide-gray-200'}`}>
                  {filteredJobs.map((job, index) => (
                    <tr key={job.job_id || `job-${index}`} className={`hover:${isDarkMode ? 'bg-[#0f1919]' : 'bg-gray-50'} transition-colors`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {job.job_id ? job.job_id.slice(0, 8) + '...' : 'Unknown ID'}
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {job.total_files || 0} file{(job.total_files || 0) !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(job.status)}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-full">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                              {job.processed_files}/{job.total_files} files
                            </span>
                            {job.status === 'processing' && job.progress_step && (
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {job.progress_step === 'processing_files' && '📁 Step 1/3'}
                                {job.progress_step === 'generating_transcript' && '📝 Step 2/3'}
                                {job.progress_step === 'analyzing_call' && '🔍 Step 3/3'}
                              </span>
                            )}
                          </div>
                          {job.status === 'processing' && (
                            <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'} mb-1`}>
                              {job.progress_message || 'Processing...'}
                            </div>
                          )}
                          <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <div 
                              className="bg-[#1a9f6b] h-2 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(Math.max((job.processed_files / job.total_files) * 100 || 0, 0), 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {job.representative_name || 'Unknown Representative'}
                          </span>
                        </div>
                      </td>
                      {shouldShowCreatedByColumn() && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <UserCheck className="w-4 h-4 text-gray-400" />
                            <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {getUserDisplayName(job)}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Timer className="w-4 h-4 text-gray-400" />
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {formatDate(job.created_at)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {job.status === 'completed' && (
                            <button
                              onClick={() => handleViewJob(job.job_id)}
                              className={`px-3 py-1.5 rounded-lg transition-all text-xs font-medium ${
                                isDarkMode
                                  ? 'bg-[#1a9f6b] hover:bg-[#148856] text-black'
                                  : 'bg-[#1a9f6b] hover:bg-[#148856] text-white'
                              }`}
                              title="View Details"
                            >
                              View Details
                            </button>
                          )}
                          {job.status === 'failed' && job.error_message && (
                            <button
                              onClick={() => toast.error('Job Failed', { description: job.error_message })}
                              className={`p-2 rounded-lg transition-all ${
                                isDarkMode
                                  ? 'hover:bg-[#1a2424] text-red-400'
                                  : 'hover:bg-gray-100 text-red-600'
                              }`}
                              title="View Error"
                            >
                              <AlertCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {filteredJobs.length > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {(['completed', 'processing', 'queued', 'failed'] as const).map((status) => {
              const count = filteredJobs.filter(job => job.status === status).length;
              return (
                <div key={status} className={`p-4 rounded-lg border ${
                  isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(status)}
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </div>
                  <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {count}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsPage;